import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidateServiceApi } from '../services/candidateServiceApi';
import { SessionCache } from '../utils/sessionCache';

/**
 * Custom hook to protect candidate routes from unauthorized access
 * Redirects to login if user is not authenticated
 * Also fetches and caches candidate profile on mount
 *
 * Usage:
 * ```tsx
 * const MyProtectedPage = () => {
 *   useAuthProtection();
 *   // ... rest of component
 * }
 * ```
 */
export const useAuthProtection = () => {
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuthAndFetchProfile = async () => {
            // Check if user is authenticated
            const token = localStorage.getItem('authToken');
            const user = localStorage.getItem('authUser');

            if (!token || !user) {
                // Not authenticated - redirect to login
                console.warn('User not authenticated, redirecting to login...');
                navigate('/auth/login', { replace: true });
                return false;
            }

            try {
                // Validate that user data is valid JSON
                const userData = JSON.parse(user);
                const candidateId = userData.id || userData.user_id || userData.candidate_id;

                if (!candidateId) {
                    console.error('No candidate ID found in user data');
                    return true;
                }

                // Check if profile is already cached
                if (SessionCache.hasCachedProfile()) {
                    console.log('Candidate profile already cached');
                    return true;
                }

                // Fetch candidate profile asynchronously and cache it using /me endpoint
                console.log('Fetching candidate profile on auth...');
                candidateServiceApi.getCandidateProfile()
                    .then(response => {
                        if (response.success && response.candidate) {
                            SessionCache.setCandidateProfile(response.candidate);
                            console.log('Candidate profile fetched and cached successfully');
                        }
                    })
                    .catch(error => {
                        console.error('Failed to fetch candidate profile on auth:', error);
                        // Don't block the user if profile fetch fails
                    });

            } catch (e) {
                console.error('Invalid user data in localStorage:', e);
                // Clear invalid data and redirect
                localStorage.removeItem('authUser');
                localStorage.removeItem('authToken');
                navigate('/auth/login', { replace: true });
                return false;
            }

            return true;
        };

        checkAuthAndFetchProfile();
    }, [navigate]);
};
